import { Navigate } from "react-router-dom";
import RoundEditor from "features/gameEditor/RoundEditor";
import GameSettings from "features/manager/settings/GameSettings";
import React from "react";
import "./App.css";
import { HelmetProvider } from "react-helmet-async";
import {
  BrowserRouter as Router,
  Route,
  Routes,
} from "react-router-dom";
import Game from "features/game/Game";
import GameControllerV2 from "features/manager/controller/GameControllerV2";
import AnonymousUserWrapper from "features/player/AnonymousUserWrapper";
import { GameWatcherWrapper } from "./features/gamestate/GameWatcherWrapper";
import GameBoardState from "./features/gameboard/GameBoardState";

import Create from "./features/manager/create/Create";
import AICreate from "./features/manager/create/ai/AICreate";
import ManualCreate from "./features/manager/create/manual/ManualCreate";

import Homepage from "./features/homepage/Homepage";
import NotFound from "./features/notFound/NotFound";
import TeamBuilding from "./features/teamBuilding/TeamBuilding";
import About from "./features/about/About";
import Contact from "./features/contact/Contact";
import Blog from "./features/blog/Blog";
import BlogPost from "./features/blog/BlogPost";
import ShareCardPreview from "./features/superlatives/preview/ShareCardPreview";
import { FullScreenSpinner } from "./features/components/FullScreenSpinner";

import logging from "utils/logging";
import AuthedUserRoute from "./features/auth/AuthedUserRoute";
import { ManagerHome } from "./features/manager/managerHome/ManagerHome";
import ManagerWrapper from "./features/manager/ManagerWrapper";
import ManagedGameHome from "./features/manager/manageGame/ManagedGameHome";
import GameHome from "./features/manager/manageGame/GameHome";
import GameBoardHostOverlay from "./features/host/components/GameBoardHostOverlay";
import DebugFinalReveal from "./features/manager/components/DebugFinalReveal";
import { AuthProviderComponent } from "provider/auth";
import GroupardyAuthModal from "./features/auth/GroupardyAuthModal";
import GroupardyAuth from "features/auth/GroupardyAuth";
import Logout from "features/auth/Logout";
import AnalyticsProvider from "analytics/AnalyticsProvider";
import ErrorBoundary from "components/ErrorBoundary";
import ComponentShowcase from "./design/ComponentShowcase";

import { CssBaseline } from '@mui/material';

const log = logging.getLogger("App (Router)"); //eslint-disable-line no-unused-vars

function primaryRouter() {
  return (
    <Routes>
      {/* Index */}
      <Route path="/" element={<Homepage />} />
      
      {/* SEO landing page: virtual team building trivia */}
      <Route path="/virtual-team-building-trivia" element={<TeamBuilding />} />

      {/* About page */}
      <Route path="/about" element={<About />} />

      {/* Contact page */}
      <Route path="/contact" element={<Contact />} />

      {/* Blog */}
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />

      {/* Share card mockup preview */}
      <Route path="/preview/share-cards" element={<ShareCardPreview />} />

      {/* Branded loading screen preview */}
      <Route path="/loading" element={<FullScreenSpinner />} />

      {/* Component library showcase (public, no auth) */}
      <Route path="/design/components" element={<ComponentShowcase />} />

      {/* Anonymous users: game playing */}
      <Route path="/game/:roomId" element={
        <AnonymousUserWrapper>
          <GameWatcherWrapper>
            <Game />
          </GameWatcherWrapper>
        </AnonymousUserWrapper>
      } />
      
      {/* Gameboard - needs to be outside manager wrapper for clean view */}
      <Route path="/gameboard/:gameId" element={
        <GameWatcherWrapper>
          <GameBoardHostOverlay>
            <GameBoardState />
          </GameBoardHostOverlay>
        </GameWatcherWrapper>
      } />

      {/* Create Game routes - not authenticated */}
      <Route path="create" element={<Create />} />
      <Route path="create/ai" element={<AICreate />} />
      <Route path="create/manual" element={<ManualCreate />} />

      {/* Auth routes */}
      <Route path="/signup" element={<GroupardyAuth />} />
      <Route path="/login" element={<GroupardyAuth />} />
      <Route path="/logout" element={<Logout />} />

      {/* Manager routes only below */}
      <Route path="/auth" element={<GroupardyAuth />} />

      {/* All internal/manager facing app */}
      <Route path="/manage/*" element={
        <AuthedUserRoute redirect="/auth">
          <ManagerWrapper />
        </AuthedUserRoute>
      }>
        <Route index element={<ManagerHome />} />

        {/* AI create retry route — deep-links to progress screen for a failed game */}
        <Route path="create/ai/:gameId" element={<AICreate />} />

        {/* AI create progress route — in-progress screen with browser-back support */}
        <Route path="create/ai/:gameId/progress" element={<AICreate />} />

        {/* AI create done route — done screen has its own URL so browser back works */}
        <Route path="create/ai/:gameId/done" element={<AICreate />} />

        {/* Game management routes with GameWatcherWrapper */}
        <Route path="game/:gameId/*" element={
          <GameWatcherWrapper>
            <Routes>
              {/* Game management routes */}
              <Route path="/" element={<ManagedGameHome />}>
                {/* index route renders GameHome (was: empty Outlet) */}
                <Route index element={<GameHome />} />
                <Route path="editor" element={<Navigate to="round1" replace />} />
                {/* Game Details page retired — name/description editing lives
                    inline on Game Home. Redirect old links to the hub. */}
                <Route path="details" element={<Navigate to=".." replace />} />
                <Route path="editor/:round" element={<RoundEditor />} />
                <Route path="settings" element={<GameSettings />} />
                {/* control-room is the new canonical path */}
                <Route path="control-room" element={<GameControllerV2 />} />
                {/* backward-compat: old /controller links keep working */}
                <Route path="controller" element={<Navigate to="../control-room" replace />} />
              </Route>
              <Route path="debug-final" element={<DebugFinalReveal />} />
            </Routes>
          </GameWatcherWrapper>
        } />
      </Route>

      {/* Catch-all: unmatched paths render the branded 404 page */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <CssBaseline />
        <Router>
          <AuthProviderComponent>
            <GroupardyAuthModal />
            <AnalyticsProvider>
              {primaryRouter()}
            </AnalyticsProvider>
          </AuthProviderComponent>
        </Router>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
